import type { Actor } from './actor';
import type { InsightEvent } from './insight-event';
import type { RoleScope } from './role';

enum AccessEventKind {
  CreateOrgRole = 'create_org_role',
  DeactivateOrgRole = 'deactivate_org_role',
  UpdateOrgRole = 'update_org_role',
  UpdateUserData = 'update_user_data',
  DeleteUserData = 'delete_user_data',
  DecryptUserData = 'decrypt_user_data',
  CreateUserAnnotation = 'create_user_annotation',
  CreateOrgApiKey = 'create_org_api_key',
  DecryptOrgApiKey = 'decrypt_org_api_key',
  UpdateOrgApiKey = 'update_org_api_key',
  InviteOrgMember = 'invite_org_member',
  UpdateOrgMember = 'update_org_member',
  DeactivateOrgMember = 'deactivate_org_member',
  // TODO - none below in BE headers yet
  CreatePlaybook = 'create_playbook',
  DisablePlaybook = 'disable_playbook',
  EditPlaybook = 'edit_playbook',
  ManuallyReviewedUser = 'manually_reviewed_entity',
}

type AccessEventDetailWrapper<T> = {
  kind: AccessEventKind;
  data: T;
};

type CreateOrgRoleDetail = AccessEventDetailWrapper<{
  roleName: string;
  scopes: RoleScope[];
  tenantRoleId: string;
}>;

type UpdateUserDataDetail = AccessEventDetailWrapper<{
  roleName: string;
  newScopes: RoleScope[];
  prevScopes: RoleScope[];
}>;

type DeleteUserDataDetail = AccessEventDetailWrapper<unknown>;

type DecryptUserDataDetail = AccessEventDetailWrapper<{
  fpId: string;
  reason: string;
  decryptedFields: string[];
}>;

type UpdateOrgRoleDetail = AccessEventDetailWrapper<{
  roleName: string;
  prevScopes: RoleScope[];
  newScopes: RoleScope[];
  tenantRoleId: string;
}>;
type CreateUserAnnotationDetail = AccessEventDetailWrapper<unknown>;

type CreateOrgApiKeyDetail = AccessEventDetailWrapper<unknown>;

type DecryptOrgApiKeyDetail = AccessEventDetailWrapper<unknown>;

type UpdateOrgApiKeyDetail = AccessEventDetailWrapper<unknown>;

type InviteOrgMemberDetail = AccessEventDetailWrapper<{
  email: string;
  tenantRoleName: string;
  tenantRoleId: string;
  firstName: string;
  lastName: string;
  scopes: RoleScope[];
}>;

type UpdateOrgMemberDetail = AccessEventDetailWrapper<{
  tenant_role_id: string;
  prev_scopes: RoleScope[];
  new_scopes: RoleScope[];
}>;

type DeactivateOrgMemberDetail = AccessEventDetailWrapper<{
  tenantRoleId: string;
  roleName: string;
}>;

type CreatePlaybookDetail = AccessEventDetailWrapper<unknown>;

type DisablePlaybookDetail = AccessEventDetailWrapper<unknown>;

type EditPlaybookDetail = AccessEventDetailWrapper<unknown>;

type ManuallyReviewedUserDetail = AccessEventDetailWrapper<unknown>;

type DeactivateOrgRoleDetail = AccessEventDetailWrapper<{
  roleName: string;
  scopes: RoleScope[];
}>;

type AccessEventDetailMap = {
  [AccessEventKind.CreateOrgRole]: CreateOrgRoleDetail;
  [AccessEventKind.UpdateUserData]: UpdateUserDataDetail;
  [AccessEventKind.DeleteUserData]: DeleteUserDataDetail;
  [AccessEventKind.DecryptUserData]: DecryptUserDataDetail;
  [AccessEventKind.CreateUserAnnotation]: CreateUserAnnotationDetail;
  [AccessEventKind.CreateOrgApiKey]: CreateOrgApiKeyDetail;
  [AccessEventKind.DecryptOrgApiKey]: DecryptOrgApiKeyDetail;
  [AccessEventKind.UpdateOrgApiKey]: UpdateOrgApiKeyDetail;
  [AccessEventKind.InviteOrgMember]: InviteOrgMemberDetail;
  [AccessEventKind.UpdateOrgMember]: UpdateOrgMemberDetail;
  [AccessEventKind.DeactivateOrgMember]: DeactivateOrgMemberDetail;
  [AccessEventKind.CreatePlaybook]: CreatePlaybookDetail;
  [AccessEventKind.DisablePlaybook]: DisablePlaybookDetail;
  [AccessEventKind.EditPlaybook]: EditPlaybookDetail;
  [AccessEventKind.ManuallyReviewedUser]: ManuallyReviewedUserDetail;
  [AccessEventKind.DeactivateOrgRole]: DeactivateOrgRoleDetail;
  [AccessEventKind.UpdateOrgRole]: UpdateOrgRoleDetail;
};

type AccessEvent<T extends AccessEventKind = AccessEventKind> = {
  id: string;
  timestamp: string;
  tenantId: string;
  name: T;
  principal?: Actor;
  insightEvent?: InsightEvent;
  detail: AccessEventDetailMap[T];
};

type TransformedAccessEvent = {
  targets: string[];
  kind: AccessEventKind;
  fpId: string;
  reason?: string;
  tenantId: string;
  timestamp: string;
  principal: Actor;
  insightEvent?: InsightEvent;
};

export type {
  AccessEvent,
  AccessEventDetailMap,
  CreateOrgApiKeyDetail,
  CreateOrgRoleDetail,
  CreatePlaybookDetail,
  CreateUserAnnotationDetail,
  DeactivateOrgMemberDetail,
  DeactivateOrgRoleDetail,
  DecryptOrgApiKeyDetail,
  DecryptUserDataDetail,
  DeleteUserDataDetail,
  DisablePlaybookDetail,
  EditPlaybookDetail,
  InviteOrgMemberDetail,
  ManuallyReviewedUserDetail,
  TransformedAccessEvent,
  UpdateOrgApiKeyDetail,
  UpdateOrgMemberDetail,
  UpdateOrgRoleDetail,
  UpdateUserDataDetail,
};
export { AccessEventKind };
